import React, { useEffect } from "react";
import {Route, Routes, Navigate} from "react-router-dom";
import { useSelector, useDispatch } from 'react-redux';

import Header from "./components/header/Header";
import Footer from "./components/footer/Footer";
import Authentication from "./components/auth/Authentication";
import Spinner from "./components/common/spinner/Spinner";
//import ContentWrapper from "./components/common/wrapper/ContentWrapper";

//import { getUsers } from "./store/users/user-actions";
import { getSkills, getLanguages } from "./store/skills/skill-actions";
import { getSkillGroups } from "./store/skillGroups/skillGroup-actions";
import { getGroups } from "./store/groups/group-actions";
import { getQueues } from "./store/queues/queue-actions";
import { getTeams } from "./store/teams/team-actions";
import { getDivisions } from "./store/divisions/division-actions";
import { getSavedFilters } from "./store/filters/filter-actions";


import * as routeConstants from "./constants/routeConstants";

import { getAbortSignal } from "./util/signalHelper";
import SkillSchedulerForm from "./pages/skillSchedules/form/SkillSchedulerForm";

const SkillLevels = React.lazy(() => import("./pages/skills/SkillLevels"));
const Users = React.lazy(() => import("./pages/users/Users"));
const Skills = React.lazy(() => import("./pages/skills/Skills"));
const SkillGroups = React.lazy(() => import("./pages/skillGroups/SkillGroups"));
const Queues = React.lazy(() => import("./pages/queues/Queues"));
const QueueMembers = React.lazy(() => import("./pages/queues/QueueMembers"));
const QueueActivations = React.lazy(() => import("./pages/queues/QueueActivations"));
const Home = React.lazy(() => import("./pages/home/Home"));
const SkillSchedules = React.lazy(() => import ("./pages/skillSchedules/SkillSchedules"));

const IS_DEV = process.env.REACT_APP_IS_DEV === 'true';
const ENABLE_SKILL_SCHEDULER = process.env.REACT_APP_ENABLE_SKILL_SCHEDULER === 'true';
const PROFILE_FIELD_SAVED_FILTERS = process.env.REACT_APP_PROFILE_FIELD_SAVED_FILTERS;

const App = () =>  {
  const dispatch = useDispatch();
  const {token, profile, isLoggedIn, hasSkillGroupPermission, hasTeamPermission} = useSelector(state => state.auth);
  const {isLoading} = useSelector(state => state.loading);

  useEffect(() => {
    const [/*userSignal,*/ userController] = getAbortSignal();
    const [skillSignal, skillController] = getAbortSignal();
    const [skillGroupSignal, skillGroupController] = getAbortSignal();
    const [languageSignal, languageController] = getAbortSignal();
    const [queueSignal, queueController] = getAbortSignal();
    const [groupSignal, groupController] = getAbortSignal();
    const [teamSignal, teamController] = getAbortSignal();
    const [divisionSignal, divisionController] = getAbortSignal();
    const [filterSignal, filterController] = getAbortSignal();

    console.log("App -> useEffect");
    if (!token) return;
//    if (!profile) return;   //Commented out -> users coming in through hop org don't have profile it seems

    console.count("App -> useEffect: getUsers");
    //Don't get all users immediately -> use filters
//    dispatch(getUsers(token, userSignal));

    console.count("App -> useEffect: getSkills");
    dispatch(getSkills(token, skillSignal));
    console.count("App -> useEffect: getLanguages");
    dispatch(getLanguages(token, languageSignal));

//    if(hasSkillGroupPermission){
      console.count("App -> useEffect: getSkillGroups");
      dispatch(getSkillGroups(token, skillGroupSignal));
/*    } else {
      console.count("App -> useEffect: getSkillGroups - missing permission");
    }
*/
    console.count("App -> useEffect: getQueues");
    dispatch(getQueues(token, queueSignal));

    //If group selector enabled
    console.count("App -> useEffect: getGroups");
    dispatch(getGroups(token, groupSignal));

    if(hasTeamPermission){
      console.count("App -> useEffect: getTeams");
      dispatch(getTeams(token, teamSignal));
    } else {
      console.count("App -> useEffect: getTeams - missing permission");
    }

    console.count("App -> useEffect: getDivisions");
    dispatch(getDivisions(token, divisionSignal));

    if(!profile || !profile.id) return;
     
    if(PROFILE_FIELD_SAVED_FILTERS){
      console.count("App -> useEffect: get saved filters");
      dispatch(getSavedFilters(token, profile.id ,filterSignal));
    }
    

    //profile and token will actually protect this useEffect from executing twice in react strict mode
    return () => {
      console.count("Authentication -> cancel profile");
      console.count("App -> cancel getUsers");
      if(userController && typeof userController.abort === "function") userController.abort();

      console.count("App -> cancel getSkills");
      skillController.abort();
      console.count("App -> cancel getLanguages");
      languageController.abort();

      console.count("App -> cancel getSkillGroups");
      skillGroupController.abort();

      console.count("App -> cancel getQueues");
      queueController.abort();
      console.count("App -> cancel getGroups");
      groupController.abort();

      if(hasTeamPermission){
        console.count("App -> cancel getTeams");
        teamController.abort();
      }

      console.count("App -> cancel getDivisions");
      divisionController.abort();
      
      console.count("App -> cancel getSavedFilters");
      filterController.abort();
    }

  }, [dispatch, token, profile, hasSkillGroupPermission, hasTeamPermission]);

  return (
    <Authentication>
      <Header />
        <React.Suspense fallback={<Spinner />}>
          <Routes>
            <Route path={routeConstants.ROUTE_DEFAULT} element={<Home />} />
            {isLoggedIn && <Route path={routeConstants.ROUTE_SKILL_LEVELS} element={<SkillLevels />} />}
            {IS_DEV && isLoggedIn && <Route path={routeConstants.ROUTE_SKILLS} element={<Skills />} />}
            {IS_DEV && isLoggedIn && <Route path={routeConstants.ROUTE_USERS} element={<Users />} />}
            {IS_DEV && isLoggedIn && <Route path={routeConstants.ROUTE_QUEUES} element={<Queues />} />}
            {isLoggedIn && <Route path={routeConstants.ROUTE_QUEUE_MEMBERS} element={<QueueMembers />} />}
            {isLoggedIn && <Route path={routeConstants.ROUTE_QUEUE_ACTIVATION} element={<QueueActivations />} />}
            {isLoggedIn && hasSkillGroupPermission && <Route path={routeConstants.ROUTE_SKILL_GROUPS} element={<SkillGroups />} />}
            {ENABLE_SKILL_SCHEDULER && isLoggedIn && <Route path={routeConstants.ROUTE_SKILL_SCHEDULER} element={<SkillSchedules />} />}
            {ENABLE_SKILL_SCHEDULER && isLoggedIn && <Route path={`${routeConstants.ROUTE_SKILL_SCHEDULER}/create`} element={<SkillSchedulerForm isCreate={true}/>} />}
            {ENABLE_SKILL_SCHEDULER && isLoggedIn && <Route path={`${routeConstants.ROUTE_SKILL_SCHEDULER}/edit/:scheduleName`} element={<SkillSchedulerForm />} />}
            <Route path={routeConstants.ROUTE_AUTH} element={<></>} />
            <Route path="*" element={<Navigate replace to="/" />} />
          </Routes>
        </React.Suspense>
      <Footer />
      {isLoading && <Spinner />}
    </Authentication>
  );
}

export default App;
